import { abApi, type AbEventType, type AbVariant } from '@/api/ab'

/**
 * A/B experiment for the code-agents interface.
 *
 *  variant "a" — the original inbox/timeline layout (CodeAgentsView.vue)
 *  variant "b" — the second experimental layout   (CodeAgentsViewV2.vue)
 *
 * Assignment is sticky per browser: we store the variant + a stable anonymous
 * `client_id` in localStorage. A `?v=a` or `?v=b` query param overrides the
 * stored value (so managers can share direct links). Telemetry is sent to
 * the backend `/api/ab/event` endpoint — network errors are swallowed since
 * we don't want to break the app because a stats write failed.
 */

export const AB_VARIANTS: AbVariant[] = ['a', 'b']
export const AB_VARIANT_STORAGE_KEY = 'b2c.ab.variant'
export const AB_CLIENT_ID_STORAGE_KEY = 'b2c.ab.client_id'
export const AB_QUERY_PARAM = 'v'

export function isValidVariant(value: unknown): value is AbVariant {
  return value === 'a' || value === 'b'
}

function readOverride(): AbVariant | null {
  if (typeof window === 'undefined') return null
  try {
    const url = new URL(window.location.href)
    const raw = url.searchParams.get(AB_QUERY_PARAM)
    if (!raw) return null
    const lower = raw.toLowerCase()
    return isValidVariant(lower) ? lower : null
  } catch {
    return null
  }
}

function readStoredVariant(): AbVariant | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(AB_VARIANT_STORAGE_KEY)
    return isValidVariant(raw) ? raw : null
  } catch {
    return null
  }
}

function writeStoredVariant(variant: AbVariant): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(AB_VARIANT_STORAGE_KEY, variant)
  } catch {
    /* ignore quota errors, private mode, etc. */
  }
}

function pickRandom(): AbVariant {
  const roll = typeof crypto !== 'undefined' && 'getRandomValues' in crypto
    ? crypto.getRandomValues(new Uint32Array(1))[0] / 0xffffffff
    : Math.random()
  return roll < 0.5 ? 'a' : 'b'
}

export function readClientId(): string {
  if (typeof window === 'undefined') return 'ssr'
  try {
    let id = window.localStorage.getItem(AB_CLIENT_ID_STORAGE_KEY)
    if (id && id.length >= 8) return id
    const bytes = new Uint8Array(16)
    if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
      crypto.getRandomValues(bytes)
    } else {
      for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
    }
    id = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    window.localStorage.setItem(AB_CLIENT_ID_STORAGE_KEY, id)
    return id
  } catch {
    return 'unknown'
  }
}

let _cachedVariant: AbVariant | null = null

export function resolveVariant(): AbVariant {
  if (_cachedVariant) return _cachedVariant
  const override = readOverride()
  if (override) {
    writeStoredVariant(override)
    _cachedVariant = override
    return override
  }
  const stored = readStoredVariant()
  if (stored) {
    _cachedVariant = stored
    return stored
  }
  const picked = pickRandom()
  writeStoredVariant(picked)
  _cachedVariant = picked
  return picked
}

export function setVariant(variant: AbVariant): AbVariant {
  writeStoredVariant(variant)
  _cachedVariant = variant
  return variant
}

export function otherVariant(current: AbVariant): AbVariant {
  return current === 'a' ? 'b' : 'a'
}

export function variantLabel(variant: AbVariant): string {
  return variant === 'a' ? 'Inbox (A)' : 'Workspace (B)'
}

export function variantPath(variant: AbVariant): string {
  return variant === 'a' ? '/app/code-agents' : '/app/code-agents-v2'
}

export function directShareUrl(variant: AbVariant): string {
  if (typeof window === 'undefined') return variantPath(variant)
  return `${window.location.origin}${variantPath(variant)}?${AB_QUERY_PARAM}=${variant}`
}

const _sent = new Set<string>()

/**
 * Fire and forget event send.
 *
 * `dedupeKey`, if provided, prevents the same event from being posted twice
 * during one browser session (useful for `assign`/`view` which we only want
 * once per variant per load).
 */
export function trackAbEvent(
  variant: AbVariant,
  event_type: AbEventType,
  meta: Record<string, unknown> = {},
  dedupeKey?: string,
): void {
  if (dedupeKey) {
    const key = `${variant}:${event_type}:${dedupeKey}`
    if (_sent.has(key)) return
    _sent.add(key)
  }
  const client_id = readClientId()
  void abApi
    .recordEvent({ variant, event_type, client_id, meta })
    .catch(() => {
      /* telemetry failures never surface to the user */
    })
}

export function trackAssignment(variant: AbVariant): void {
  trackAbEvent(variant, 'assign', { source: 'auto' }, 'once')
}

export function trackView(variant: AbVariant, view: string): void {
  trackAbEvent(variant, 'view', { view }, view)
}
